import classNames from "classnames";
import React from "react";
import { formatDay } from "../../../format";
import { DayAvailability } from "../../../messages";
import { STRINGS } from "../../../strings";
import { hoursToDisplayTime } from "../../../time";
import { translate } from "../../../translate";
import { disableScroll, enableScroll } from "../../../utils/scroll";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import { AppSlider } from "../../Generic/AppSlider";
import { AppSliderHandlerProps } from "../../Generic/AppSliderHandler";
import Flex from "../../Generic/Flex";
import SwitchListItem from "../../Generic/SwitchListItem/SwitchListItem";

interface Props {
  day: DayAvailability;
  onChangeTimes: (value: [number, number], day: DayAvailability) => void;
  onChangeBusyAllDay: (busyAllDay: boolean, day: DayAvailability) => void;
}

const DayAvailabilityCard = (props: Props) => {
  const handleChangeSlider = React.useCallback(
    (value: number | number[] | null | undefined) => {
      if (!Array.isArray(value) || value.length < 2) {
        throw new Error(`Got invalid value of handleChange in AvailabilityCard (${value})`);
      }

      props.onChangeTimes.call(null, [value[0], value[1]], props.day);
    },
    [props.onChangeTimes, props.day]
  );

  const handleChangeBusyAllDay = React.useCallback(() => {
    props.onChangeBusyAllDay.call(null, !props.day.busyAllDay, props.day);
  }, [props.day, props.onChangeBusyAllDay]);

  const readableAvailabiliy = React.useMemo(() => {
    if (props.day.busyAllDay) {
      return translate(STRINGS.NOT_AVAILABLE);
    }

    if (props.day.startHour === 0 && props.day.endHour === 24) {
      return `${translate(STRINGS.IM_AVAILABLE)} ${translate(STRINGS.ALL_DAY)}`;
    }

    const fromHour = hoursToDisplayTime(props.day.startHour);
    const toHour = hoursToDisplayTime(props.day.endHour);

    return `${translate(STRINGS.IM_AVAILABLE)} ${fromHour} - ${toHour}`;
  }, [props.day.busyAllDay, props.day.endHour, props.day.startHour]);

  const title: React.ReactNode = (
    <div className="__title">
      <strong>{formatDay(props.day.day)}, </strong>
      <span>{readableAvailabiliy}</span>
    </div>
  );

  const sliderStartHandler = React.useMemo(
    (): AppSliderHandlerProps => ({
      direction: "start",
      disabled: props.day.busyAllDay,
      children: hoursToDisplayTime(props.day.startHour),
    }),
    [props.day.busyAllDay, props.day.startHour]
  );

  const sliderEndHandler = React.useMemo(
    (): AppSliderHandlerProps => ({
      direction: "end",
      disabled: props.day.busyAllDay,
      children: hoursToDisplayTime(props.day.endHour),
    }),
    [props.day.busyAllDay, props.day.endHour]
  );

  const slider = React.useMemo(
    () => (
      <div className="__slider-container">
        <AppSlider
          // https://github.com/mpowaga/react-slider/issues/137
          onBeforeChange={disableScroll}
          onAfterChange={enableScroll}
          withBars={true}
          disabled={props.day.busyAllDay}
          max={24}
          min={0}
          minDistance={3}
          value={[props.day.startHour, props.day.endHour]}
          step={1}
          onChange={handleChangeSlider}
          snapDragDisabled={true}
          startHandler={sliderStartHandler}
          endHandler={sliderEndHandler}
        />
      </div>
    ),
    [props.day, sliderStartHandler, sliderEndHandler, handleChangeSlider]
  );

  const busyAllDayCheckbox: React.ReactNode = React.useMemo(
    () => (
      <SwitchListItem
        checked={props.day.busyAllDay}
        label={translate(STRINGS.BUSY_ALL_DAY)}
        onChange={handleChangeBusyAllDay}
      />
    ),
    [props.day.busyAllDay, handleChangeBusyAllDay]
  );

  return (
    <AppCard className={classNames("DayAvailabilityCard", { disabled: props.day.busyAllDay })}>
      <AppCardContent>
        <Flex direction="column" yGap={16}>
          {title}
          {slider}
          {busyAllDayCheckbox}
        </Flex>
      </AppCardContent>
    </AppCard>
  );
};

export default React.memo(DayAvailabilityCard);
